import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../components/common/Modal';
import { shippingApi } from '../../api/shippingApi';
import {
  formatShareDate,
  formatShareNumber,
} from './supplierShareColumnDefs';
import { parseDirectAddress } from './directAddressUtils';
import {
  buildRequestFormViewModel,
  constrainSelectionToSameShipmentDate,
  expandRequestFormCandidates,
  getCandidateSelectability,
  keysForShipmentDate,
  normalizeDateKey,
} from './requestFormUtils';

/** 明細スロット数（空き行含む） */
const DETAIL_SLOT_COUNT = 6;

function formatQty(qty, tani) {
  if (qty == null) return '-';
  const n = formatShareNumber(qty);
  return tani ? `${n} ${tani}` : n;
}

function formatWeight(w) {
  if (w == null || Number.isNaN(Number(w))) return '';
  return `${formatShareNumber(Number(w))} kg`;
}

function formatUnitWeight(w) {
  if (w == null || Number.isNaN(Number(w))) return '';
  return formatShareNumber(Number(w));
}

function displayHeaderDate(value) {
  if (!value || value === '-') return '';
  if (value === '（明細により異なる）') return value;
  return formatShareDate(value);
}

/** 着日欄: 日付なら年月日、出荷形態などそれ以外はそのまま */
function displayArrivalCell(value) {
  if (!value || value === '-') return '';
  if (value === '（明細により異なる）') return value;
  if (/^\d{4}-\d{2}-\d{2}/.test(String(value))) {
    return formatShareDate(value);
  }
  return String(value);
}

function displayText(value) {
  if (value == null || value === '-' || value === '') return '';
  return value;
}

/** 帳票用: 送り先住所を大きめフォントで表示 */
function FormAddress({ address, tel }) {
  const parsed = parseDirectAddress(
    address === '-' ? '' : address,
    tel === '-' ? '' : tel
  );
  if (parsed.isEmpty) return '\u00A0';
  return (
    <div className="w-full whitespace-normal break-words leading-snug">
      {parsed.postalCode ? (
        <div className="text-[13px] font-semibold">〒{parsed.postalCode}</div>
      ) : null}
      {parsed.address ? <div>{parsed.address}</div> : null}
      {parsed.tel ? <div className="text-[13px] font-semibold">℡ {parsed.tel}</div> : null}
    </div>
  );
}

/**
 * ラベル上・値下の枠セル。
 * 横置きラベルだと値幅が足りず No / 日付が枠外にはみ出すため、上ラベルで横幅を確保する。
 */
function FieldCell({
  label,
  children,
  nowrap = false,
  align = 'left',
  valueMinClass = '',
}) {
  const alignClass =
    align === 'right'
      ? 'justify-end text-right'
      : align === 'center'
        ? 'justify-center text-center'
        : 'justify-start text-left';
  return (
    <div className="h-full min-w-0 flex flex-col">
      <div className="shrink-0 px-2 py-1 bg-slate-50 border-b border-black text-[12px] leading-none text-slate-700 font-semibold tracking-wide">
        {label}
      </div>
      <div
        className={`flex-1 min-w-0 px-2.5 py-2 text-[15px] leading-snug text-slate-900 font-bold flex ${
          valueMinClass ? 'items-start' : 'items-center'
        } ${alignClass} ${nowrap ? 'whitespace-nowrap' : 'break-words'} ${valueMinClass}`}
      >
        {children || '\u00A0'}
      </div>
    </div>
  );
}

function padDetailSlots(detailLines) {
  const rows = Array.isArray(detailLines) ? [...detailLines] : [];
  while (rows.length < DETAIL_SLOT_COUNT) {
    rows.push({ key: `__empty-${rows.length}`, empty: true });
  }
  return rows;
}

function footerText(lines, single) {
  if (lines) {
    return lines.map((l) => `[${l.no}] ${l.text}`).join('\n');
  }
  return displayText(single);
}

export function RequestFormPreview({ viewModel }) {
  if (!viewModel) return null;
  const { header, destination, flags, detailLines, footer } = viewModel;
  const slots = padDetailSlots(detailLines);
  const extraCols =
    (flags.showDetailShukka ? 1 : 0) +
    (flags.showDetailArrival ? 1 : 0) +
    (flags.showDetailNonyusaki ? 1 : 0) +
    (flags.showDetailDestination ? 2 : 0);

  return (
    <div className="request-form-sheet bg-white text-slate-900 leading-tight">
      {/* ヘッダは同一5列テーブルで縦線を揃える */}
      <table className="w-full border-collapse border-2 border-black table-fixed request-form-header-table">
        <colgroup>
          <col style={{ width: '24%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '28%' }} />
        </colgroup>
        <tbody>
          <tr className="rf-title-row">
            <td colSpan={3} className="border border-black">
              <div className="rf-title-cell-inner">
                <h1 className="m-0 text-[20px] font-bold tracking-wide leading-none">
                  {viewModel.title}
                </h1>
                {header.request_no ? (
                  <div className="mt-1 text-[12px] font-semibold text-slate-700">
                    依頼No {header.request_no}
                  </div>
                ) : null}
              </div>
            </td>
            <td className="border border-black bg-slate-50">
              <div className="rf-title-cell-inner text-[12px] font-semibold text-slate-700 whitespace-nowrap">
                出荷形態
              </div>
            </td>
            <td className="border border-black">
              <div className="rf-title-cell-inner text-[15px] font-bold">
                {displayText(header.shukka_type) || '\u00A0'}
              </div>
            </td>
          </tr>

          {/* ラベル行と内容行を分け、縦の枠線を延長して揃える */}
          <tr className="bg-slate-50">
            <td className="border border-black px-2 py-1 text-[12px] font-semibold text-slate-700 tracking-wide align-middle">
              受注No
            </td>
            <td className="border border-black px-2 py-1 text-[12px] font-semibold text-slate-700 tracking-wide align-middle">
              依頼日
            </td>
            <td className="border border-black px-2 py-1 text-[12px] font-semibold text-slate-700 tracking-wide align-middle">
              依頼作成者
            </td>
            <td className="border border-black px-2 py-1 text-[12px] font-semibold text-slate-700 tracking-wide align-middle">
              営業
            </td>
            <td className="border border-black px-2 py-1 text-[12px] font-semibold text-slate-700 tracking-wide align-middle">
              発注No
            </td>
          </tr>
          <tr>
            <td className="border border-black px-2.5 py-2 text-[15px] font-bold whitespace-nowrap align-middle">
              {displayText(header.juchu_no) || '\u00A0'}
            </td>
            <td className="border border-black px-2.5 py-2 text-[15px] font-bold whitespace-nowrap align-middle">
              {displayHeaderDate(header.issued_date) || '\u00A0'}
            </td>
            <td className="border border-black px-2.5 py-2 text-[15px] font-bold align-middle">
              {displayText(header.issued_by) || '\u00A0'}
            </td>
            <td className="border border-black px-2.5 py-2 text-[15px] font-bold align-middle">
              {displayText(header.tanto_shain_name) || '\u00A0'}
            </td>
            <td className="border border-black px-2.5 py-2 text-[15px] font-bold whitespace-nowrap align-middle">
              {displayText(header.hacchu_no) || '\u00A0'}
            </td>
          </tr>

          <tr>
            <td colSpan={3} className="border border-black p-0 align-top">
              <FieldCell label="得意先">{displayText(header.customer_name)}</FieldCell>
            </td>
            <td colSpan={2} className="border border-black p-0 align-top">
              <FieldCell label="納入先">{displayText(header.nonyusaki)}</FieldCell>
            </td>
          </tr>

          <tr>
            <td colSpan={5} className="border border-black p-0 align-top">
              <FieldCell label="現場">{displayText(header.genba_name)}</FieldCell>
            </td>
          </tr>

          <tr>
            <td colSpan={2} className="border border-black p-0 align-top">
              <FieldCell label="発送日" nowrap>
                {displayHeaderDate(header.shipment_date)}
              </FieldCell>
            </td>
            <td colSpan={2} className="border border-black p-0 align-top">
              <FieldCell label="着日" nowrap>
                {displayArrivalCell(header.arrival_date)}
              </FieldCell>
            </td>
            <td className="border border-black p-0 align-top">
              <FieldCell label="総重量" nowrap>
                {formatWeight(header.total_weight)}
              </FieldCell>
            </td>
          </tr>
        </tbody>
      </table>

      <table className="w-full border-collapse border-x-2 border-b-2 border-black table-fixed request-form-detail-table -mt-px">
        <colgroup>
          <col style={{ width: extraCols ? '10%' : '11%' }} />
          <col style={{ width: extraCols ? '24%' : '28%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: extraCols ? '12%' : '14%' }} />
          <col style={{ width: extraCols ? '18%' : '26%' }} />
          {flags.showDetailShukka ? <col style={{ width: '8%' }} /> : null}
          {flags.showDetailArrival ? <col style={{ width: '8%' }} /> : null}
          {flags.showDetailNonyusaki ? <col style={{ width: '10%' }} /> : null}
          {flags.showDetailDestination ? (
            <>
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
            </>
          ) : null}
        </colgroup>
        <thead>
          <tr className="bg-slate-50">
            <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom">
              製品CD
            </th>
            <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom">
              製品名
              <span className="block text-[11px] font-normal text-slate-600 mt-0.5">寸法</span>
            </th>
            <th className="border border-black px-2 py-2 text-[12px] font-semibold text-right align-bottom">
              数量
            </th>
            <th className="border border-black px-2 py-2 text-[12px] font-semibold text-right align-bottom">
              <div>単重量(kg)</div>
              <div className="mt-1 pt-1 border-t border-dotted border-slate-400">重量(kg)</div>
            </th>
            <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom">
              仕入先
            </th>
            <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom">
              製品仕様
            </th>
            {flags.showDetailShukka && (
              <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom whitespace-nowrap">
                出荷形態
              </th>
            )}
            {flags.showDetailArrival && (
              <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom whitespace-nowrap">
                着日
              </th>
            )}
            {flags.showDetailNonyusaki && (
              <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom">
                納入先
              </th>
            )}
            {flags.showDetailDestination && (
              <>
                <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom">
                  送り先
                </th>
                <th className="border border-black px-2 py-2 text-[12px] font-semibold text-left align-bottom">
                  住所
                </th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {slots.map((line) => {
            const cellPad = 'border border-black px-2 py-2.5 align-top';
            const cellMid = 'border border-black px-2 py-2.5 align-middle';
            if (line.empty) {
              return (
                <tr key={line.key} className="request-form-detail-slot h-[3.6rem]">
                  <td className={cellMid}>&nbsp;</td>
                  <td className={cellPad}>
                    <div className="min-h-[1.2em]">&nbsp;</div>
                    <div className="mt-1 pt-1 border-t border-dashed border-slate-300 min-h-[1.1em]">
                      &nbsp;
                    </div>
                  </td>
                  <td className={cellMid}>&nbsp;</td>
                  <td className={cellPad}>
                    <div className="min-h-[1.2em]">&nbsp;</div>
                    <div className="mt-1 pt-1 border-t border-dotted border-slate-400 min-h-[1.1em]">
                      &nbsp;
                    </div>
                  </td>
                  <td className={cellMid}>&nbsp;</td>
                  <td className={cellPad}>&nbsp;</td>
                  {flags.showDetailShukka && <td className={cellPad}>&nbsp;</td>}
                  {flags.showDetailArrival && <td className={cellPad}>&nbsp;</td>}
                  {flags.showDetailNonyusaki && <td className={cellPad}>&nbsp;</td>}
                  {flags.showDetailDestination && (
                    <>
                      <td className={cellPad}>&nbsp;</td>
                      <td className={cellPad}>&nbsp;</td>
                    </>
                  )}
                </tr>
              );
            }
            return (
              <tr key={line.key} className="request-form-detail-slot">
                <td className={`${cellMid} text-[14px] font-bold whitespace-nowrap`}>
                  {displayText(line.product_disp)}
                </td>
                <td className={cellPad}>
                  <div className="text-[14px] font-bold break-words">
                    {displayText(line.product_name)}
                  </div>
                  <div className="mt-1 pt-1 border-t border-dashed border-slate-300 text-[13px] break-words">
                    {displayText(line.product_size)}
                  </div>
                </td>
                <td className={`${cellMid} text-right text-[14px] font-bold whitespace-nowrap`}>
                  {line.qty == null ? '' : formatShareNumber(line.qty)}
                </td>
                <td className={`${cellPad} text-right`}>
                  <div className="text-[14px] font-bold whitespace-nowrap">
                    {formatUnitWeight(line.product_weight)}
                  </div>
                  <div className="mt-1 pt-1 border-t border-dotted border-slate-400 text-[14px] font-bold whitespace-nowrap">
                    {formatUnitWeight(line.line_weight)}
                  </div>
                </td>
                <td className={`${cellMid} text-[14px] font-bold break-words`}>
                  {displayText(line.supplier_name)}
                </td>
                <td className={`${cellPad} text-[14px] break-words`}>
                  {displayText(line.product_spec)}
                </td>
                {flags.showDetailShukka && (
                  <td className={`${cellPad} text-[13px]`}>{displayText(line.shukka_type)}</td>
                )}
                {flags.showDetailArrival && (
                  <td className={`${cellPad} text-[13px] whitespace-nowrap`}>
                    {displayArrivalCell(line.arrival_cell ?? line.arrival_date)}
                  </td>
                )}
                {flags.showDetailNonyusaki && (
                  <td className={`${cellPad} text-[13px] break-words`}>
                    {displayText(line.nonyusaki)}
                  </td>
                )}
                {flags.showDetailDestination && (
                  <>
                    <td className={`${cellPad} text-[13px] break-words`}>
                      {displayText(line.juchu_direct_name)}
                    </td>
                    <td className={`${cellPad} text-[13px]`}>
                      <FormAddress
                        address={line.juchu_direct_address}
                        tel={line.juchu_direct_tel}
                      />
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      <table className="w-full border-collapse border-x-2 border-b-2 border-black table-fixed -mt-px">
        <colgroup>
          <col style={{ width: '24%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '28%' }} />
        </colgroup>
        <tbody>
          <tr>
            <td colSpan={5} className="border border-black p-0 align-top">
              <FieldCell label="送り先">{displayText(destination.name)}</FieldCell>
            </td>
          </tr>
          <tr>
            <td colSpan={5} className="border border-black p-0 align-top">
              <FieldCell label="送り先住所">
                <FormAddress address={destination.address} tel={destination.tel} />
              </FieldCell>
            </td>
          </tr>
          <tr>
            <td colSpan={5} className="border border-black p-0 align-top">
              <FieldCell label="備考" valueMinClass="min-h-[4.5rem]">
                <span className="whitespace-pre-wrap font-semibold">
                  {footerText(footer.bikoLines, footer.biko)}
                </span>
              </FieldCell>
            </td>
          </tr>
          <tr>
            <td colSpan={5} className="border border-black p-0 align-top">
              <FieldCell label="メモ" valueMinClass="min-h-[4.5rem]">
                <span className="whitespace-pre-wrap font-semibold">
                  {footerText(footer.memoLines, footer.memo)}
                </span>
              </FieldCell>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export const RequestFormModal = ({ isOpen, onClose, hacchuNo }) => {
  const [step, setStep] = useState('select'); // 'select' | 'preview'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  const resetState = useCallback(() => {
    setStep('select');
    setLoading(false);
    setError(null);
    setItems([]);
    setSelectedKeys(new Set());
  }, []);

  useEffect(() => {
    if (!isOpen || !hacchuNo) {
      resetState();
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setStep('select');
      setSelectedKeys(new Set());
      try {
        const data = await shippingApi.getSupplierSharePurchaseOrder(hacchuNo);
        if (!cancelled) {
          setItems(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) {
          setItems([]);
          const detail = e?.detail ?? e?.response?.data?.detail;
          setError(
            (typeof detail === 'string' && detail) ||
              e?.message ||
              '明細の取得に失敗しました'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, hacchuNo, resetState]);

  const candidates = useMemo(() => expandRequestFormCandidates(items), [items]);

  const anchorShipmentDateKey = useMemo(() => {
    for (const key of selectedKeys) {
      const c = candidates.find((x) => x.key === key);
      if (c) {
        const k = normalizeDateKey(c.shipment_date);
        if (k) return k;
      }
    }
    return null;
  }, [selectedKeys, candidates]);

  const anchorCandidate = useMemo(() => {
    for (const key of selectedKeys) {
      const c = candidates.find((x) => x.key === key);
      if (c && normalizeDateKey(c.shipment_date)) return c;
    }
    return null;
  }, [selectedKeys, candidates]);

  const selectedRows = useMemo(
    () => candidates.filter((c) => selectedKeys.has(c.key)),
    [candidates, selectedKeys]
  );

  const viewModel = useMemo(
    () => (step === 'preview' ? buildRequestFormViewModel(selectedRows) : null),
    [step, selectedRows]
  );

  const handleCloseRequest = useCallback(() => {
    if (step === 'preview') {
      setStep('select');
      return;
    }
    onClose();
  }, [step, onClose]);

  const toggleKey = (key, candidate) => {
    const { selectable } = getCandidateSelectability(
      candidate,
      anchorShipmentDateKey,
      anchorCandidate
    );
    if (!selectable && !selectedKeys.has(key)) return;

    const next = new Set(selectedKeys);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    const constrained = constrainSelectionToSameShipmentDate(candidates, next);
    setSelectedKeys(constrained.selectedKeys);
  };

  const handleSelectSameDate = () => {
    if (!anchorShipmentDateKey && selectedKeys.size === 0) {
      const firstSelectable = candidates.find(
        (c) => getCandidateSelectability(c).selectable
      );
      if (!firstSelectable) return;
      const dateKey = normalizeDateKey(firstSelectable.shipment_date);
      setSelectedKeys(
        new Set(keysForShipmentDate(candidates, dateKey, firstSelectable))
      );
      return;
    }
    if (!anchorShipmentDateKey) return;
    setSelectedKeys(
      new Set(keysForShipmentDate(candidates, anchorShipmentDateKey, anchorCandidate))
    );
  };

  const handleClearSelection = () => setSelectedKeys(new Set());

  const goPreview = () => {
    if (selectedKeys.size === 0) return;
    setStep('preview');
  };

  const handlePrint = () => {
    const styleId = 'request-form-print-page-style';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent =
        '@page { size: A4 portrait; margin: 10mm; }';
      document.head.appendChild(styleEl);
    }
    document.body.classList.add('request-form-print');

    const cleanup = () => {
      document.body.classList.remove('request-form-print');
      const el = document.getElementById(styleId);
      if (el) el.remove();
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1000);
  };

  if (!isOpen) return null;

  const title = (
    <div>
      <div className="text-lg font-semibold text-slate-900">
        {step === 'preview' ? '出荷依頼書プレビュー' : '出荷依頼書 — 明細選択'}
      </div>
      <div className="text-sm text-slate-600 mt-1">
        発注No: {hacchuNo || '-'}
        {anchorShipmentDateKey ? (
          <span className="ml-3">発送日: {formatShareDate(anchorShipmentDateKey)}</span>
        ) : null}
        {selectedKeys.size > 0 ? (
          <span className="ml-3">選択: {selectedKeys.size}件</span>
        ) : null}
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleCloseRequest} title={title} size="xl">
      {loading ? (
        <div className="p-8 text-center text-slate-500">読み込み中...</div>
      ) : error ? (
        <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </div>
      ) : step === 'select' ? (
        <div className="space-y-3">
          {candidates.length === 0 ? (
            <p className="text-sm text-slate-600">明細がありません。</p>
          ) : candidates.every((c) => !getCandidateSelectability(c).selectable) ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              発送日が設定された明細がありません。発送日を入れてから印刷してください。
            </p>
          ) : (
            <p className="text-xs text-slate-500">
              同一発送日・同一送り先の明細のみ選択できます。発送日未設定の行は選択できません。
            </p>
          )}

          <div className="overflow-auto max-h-[50vh] border border-slate-200 rounded">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left border-b w-10">選択</th>
                  <th className="px-2 py-2 text-left border-b">発送日</th>
                  <th className="px-2 py-2 text-left border-b">製品CD</th>
                  <th className="px-2 py-2 text-left border-b">製品名</th>
                  <th className="px-2 py-2 text-left border-b">寸法</th>
                  <th className="px-2 py-2 text-right border-b">数量</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => {
                  const { selectable, reason } = getCandidateSelectability(
                    c,
                    anchorShipmentDateKey,
                    anchorCandidate
                  );
                  const checked = selectedKeys.has(c.key);
                  const disabled = !selectable && !checked;
                  return (
                    <tr
                      key={c.key}
                      className={
                        disabled
                          ? 'bg-slate-50 text-slate-400'
                          : 'hover:bg-slate-50 cursor-pointer'
                      }
                      onClick={() => {
                        if (disabled) return;
                        toggleKey(c.key, c);
                      }}
                      title={disabled ? reason || '' : undefined}
                    >
                      <td className="px-2 py-1.5 border-b align-middle">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleKey(c.key, c)}
                          onClick={(e) => e.stopPropagation()}
                          title={disabled ? reason || '' : undefined}
                        />
                      </td>
                      <td className="px-2 py-1.5 border-b">
                        <div>{formatShareDate(c.shipment_date)}</div>
                        {disabled && reason ? (
                          <div className="text-[10px] text-slate-400">{reason}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5 border-b">{c.product_disp || '-'}</td>
                      <td className="px-2 py-1.5 border-b">{c.product_name || '-'}</td>
                      <td className="px-2 py-1.5 border-b">{c.product_size || '-'}</td>
                      <td className="px-2 py-1.5 border-b text-right whitespace-nowrap">
                        {formatQty(c.qty, c.tani)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
                onClick={handleSelectSameDate}
              >
                この発送日を全選択
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
                onClick={handleClearSelection}
              >
                選択解除
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
                onClick={onClose}
              >
                閉じる
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded bg-slate-900 text-white disabled:opacity-40"
                disabled={selectedKeys.size === 0}
                onClick={goPreview}
              >
                出荷依頼書を表示
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="request-form-print-root overflow-auto max-h-[60vh] border border-slate-200 rounded p-4 bg-white">
            <RequestFormPreview viewModel={viewModel} />
          </div>
          <div className="request-form-no-print flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
              onClick={() => setStep('select')}
            >
              選択に戻る
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50"
              onClick={onClose}
            >
              閉じる
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-sm rounded bg-slate-900 text-white"
              onClick={handlePrint}
            >
              印刷
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};
